import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Receipt, Plus, Trash, PencilSimple, Paperclip, Buildings, Lightning, FilePdf, Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const FIXED_OPERATIONAL_OPTIONS = [
  "Alquiler", "Luz", "Agua", "Basura", "Seguros", "Internet", "Telefonía", "Productos químicos", "Mantenimiento",
];

const empty = {
  concept: "", base_imponible: 0, iva_rate: 21, retention_rate: 0,
  retention_type: "ninguno",  // Sprint F · #4 · clasificación fiscal AEAT
  provider_id: "", is_fixed_operational: false, category: "",
  attachment_url: "", attachment_filename: "",
  vat_lines: [],  // tramos IVA adicionales (multi-IVA por factura)
};

const ATTACH_MAX_BYTES = 3 * 1024 * 1024; // 3 MB

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Expenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [provOpen, setProvOpen] = useState(false);
  const [newProv, setNewProv] = useState({ name: "", nif_cif: "", email: "", phone: "" });

  const load = async () => {
    const [e, p] = await Promise.all([api.get("/expenses"), api.get("/providers")]);
    setItems(e.data); setProviders(p.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      concept: it.concept || "",
      base_imponible: it.base_imponible || 0,
      iva_rate: it.iva_rate ?? 21,
      retention_rate: it.retention_rate ?? 0,
      retention_type: it.retention_type || "ninguno",
      provider_id: it.provider_id || "",
      is_fixed_operational: !!it.is_fixed_operational,
      category: it.category || "",
      attachment_url: it.attachment_url || "",
      attachment_filename: it.attachment_filename || "",
      vat_lines: Array.isArray(it.vat_lines) ? it.vat_lines.map((v) => ({ base: v.base ?? 0, rate: v.rate ?? 10 })) : [],
    });
    setOpen(true);
  };

  const addVatLine = () => {
    if ((form.vat_lines || []).length >= 4) { toast.info("Máximo 4 tramos adicionales"); return; }
    setForm((f) => ({ ...f, vat_lines: [...(f.vat_lines || []), { base: 0, rate: 10 }] }));
  };
  const updateVatLine = (idx, key, val) => {
    const next = [...(form.vat_lines || [])];
    next[idx] = { ...next[idx], [key]: val };
    setForm({ ...form, vat_lines: next });
  };
  const removeVatLine = (idx) => {
    setForm({ ...form, vat_lines: (form.vat_lines || []).filter((_, i) => i !== idx) });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > ATTACH_MAX_BYTES) { toast.error("Adjunto > 3 MB"); return; }
    const isPdf = file.type === "application/pdf";
    const isImg = file.type.startsWith("image/");
    if (!isPdf && !isImg) { toast.error("Solo PDF o imagen"); return; }
    try {
      const b64 = await fileToBase64(file);
      setForm((f) => ({ ...f, attachment_url: b64, attachment_filename: file.name }));
      toast.success(`Adjunto cargado · ${file.name}`);
    } catch { toast.error("Error leyendo el archivo"); }
  };

  const submit = async () => {
    if (!form.concept.trim()) { toast.error("Concepto obligatorio"); return; }
    setBusy(true);
    try {
      const payload = {
        concept: form.concept,
        base_imponible: Number(form.base_imponible),
        iva_rate: Number(form.iva_rate),
        retention_rate: Number(form.retention_rate),
        retention_type: form.retention_type || "ninguno",
        provider_id: form.provider_id || null,
        is_fixed_operational: !!form.is_fixed_operational,
        category: form.category || "",
        attachment_url: form.attachment_url || "",
        attachment_filename: form.attachment_filename || "",
        vat_lines: (form.vat_lines || [])
          .filter((v) => Number(v.base) > 0)
          .map((v) => ({ base: Number(v.base), rate: Number(v.rate) })),
      };
      if (editingId) await api.put(`/expenses/${editingId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editingId ? "Gasto actualizado" : "Gasto registrado");
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createProvider = async () => {
    if (!newProv.name.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      const { data } = await api.post("/providers", newProv);
      toast.success("Proveedor creado");
      setProviders((p) => [...p.filter((x) => x.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, provider_id: data.id }));
      setProvOpen(false); setNewProv({ name: "", nif_cif: "", email: "", phone: "" });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar gasto?")) return;
    await api.delete(`/expenses/${id}`); load();
  };

  const total = items.reduce((a, x) => a + (x.total || 0), 0);
  const fixed_total = items.filter((x) => x.is_fixed_operational).reduce((a, x) => a + (x.total || 0), 0);

  const isColab = user?.role === "colaborador";

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Costes operativos</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">
            {isColab ? "Mis gastos" : "Gastos"}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            {isColab
              ? "Tus gastos personales asociados al servicio (productos, desplazamientos, herramientas)."
              : "Gastos de la organización — vincula proveedor y marca los fijos operativos."}
          </p>
        </div>
        <Button onClick={startCreate} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="new-expense-btn">
          <Plus className="mr-1.5" /> Nuevo gasto
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="metric-card p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Total mes</div>
          <div className="font-display font-bold text-3xl tabular-nums mt-1">{fmtEur(total)}</div>
        </div>
        <div className="metric-card p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Fijos operativos</div>
          <div className="font-display font-bold text-3xl tabular-nums mt-1 text-amber-400">{fmtEur(fixed_total)}</div>
          <div className="text-[10px] text-gray-500 mt-1"><Lightning size={10} weight="fill" className="inline" /> Alquiler, luz, agua…</div>
        </div>
        <div className="metric-card p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Gastos registrados</div>
          <div className="font-display font-bold text-3xl tabular-nums mt-1">{items.length}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Fecha</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Concepto</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Proveedor</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Base</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">IVA</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Retención</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Total</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-10">Sin gastos registrados</TableCell></TableRow>}
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-xs">{fmtDate(e.date)}</TableCell>
                <TableCell className="text-sm font-medium">
                  <div className="flex items-center gap-1.5">
                    {e.is_fixed_operational && <span title="Gasto fijo operativo"><Lightning size={11} weight="fill" className="text-amber-500" /></span>}
                    {e.concept}
                    {e.attachment_url && (
                      <a href={e.attachment_url} target="_blank" rel="noreferrer" title={e.attachment_filename || "Adjunto"} className="text-[var(--ag-text-muted)] hover:text-[var(--ag-gold)]">
                        {e.attachment_url.startsWith("data:application/pdf") ? <FilePdf size={12} weight="fill" /> : <ImageIcon size={12} weight="fill" />}
                      </a>
                    )}
                  </div>
                  {e.category && <div className="text-[10px] text-gray-500">{e.category}</div>}
                </TableCell>
                <TableCell className="text-xs text-gray-500">
                  {e.provider_name ? <span className="inline-flex items-center gap-1"><Buildings size={10} /> {e.provider_name}</span> : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtEur(e.base_imponible)}</TableCell>
                <TableCell className="text-right tabular-nums text-gray-500">{fmtEur(e.iva_amount)} <span className="text-[10px] text-[var(--ag-text-muted)]">({e.iva_rate}%)</span></TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">-{fmtEur(e.retention_amount)} <span className="text-[10px] text-[var(--ag-text-muted)]">({e.retention_rate}%)</span></TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtEur(e.total)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={()=>startEdit(e)} data-testid={`edit-exp-${e.id}`} title="Editar"><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(e.id)} data-testid={`del-exp-${e.id}`} title="Eliminar"><Trash size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if (!o) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">{editingId ? "Editar gasto" : "Nuevo gasto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Concepto *</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.concept} onChange={(e)=>setForm({...form, concept: e.target.value})} className="rounded-sm flex-1" data-testid="exp-concept" />
                <Button type="button" variant="outline"
                  className="rounded-sm border-purple-400 text-purple-700 hover:bg-purple-50 shrink-0"
                  disabled={!form.concept?.trim()}
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/ai/expenses/classify", {
                        concept: form.concept, iva_rate: form.iva_rate, retention_rate: form.retention_rate,
                      });
                      setForm((f) => ({
                        ...f,
                        category: data.category || f.category,
                        is_fixed_operational: data.is_fixed_operational ?? f.is_fixed_operational,
                        retention_type: data.retention_type || f.retention_type,
                      }));
                      toast.success(`IA: ${data.category} · ${Math.round(data.confidence*100)}% confianza`);
                    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "IA no disponible"); }
                  }}
                  data-testid="exp-ai-classify-btn"
                  title="Sugerir categoría con IA">
                  ✨ IA
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Base</Label>
                <Input type="number" step="0.01" value={form.base_imponible} onChange={(e)=>setForm({...form, base_imponible: e.target.value})} className="mt-1 rounded-sm tabular-nums" data-testid="exp-base" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">IVA %</Label>
                <Input type="number" step="0.1" value={form.iva_rate} onChange={(e)=>setForm({...form, iva_rate: e.target.value})} className="mt-1 rounded-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Reten. %</Label>
                <Input type="number" step="0.1" value={form.retention_rate} onChange={(e)=>setForm({...form, retention_rate: e.target.value})} className="mt-1 rounded-sm tabular-nums" />
              </div>
            </div>
            {/* Sprint F · #4 · Clasificación fiscal AEAT (modelos 111 / 115) */}
            {Number(form.retention_rate) > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="exp-retention-type-section">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-amber-800 font-semibold">Tipo de retención (AEAT)</Label>
                <Select value={form.retention_type || "ninguno"} onValueChange={(val)=>setForm({...form, retention_type: val})}>
                  <SelectTrigger className="mt-1.5 rounded-sm h-9 bg-white" data-testid="exp-retention-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profesional">Profesional autónomo (15%) → Modelo 111</SelectItem>
                    <SelectItem value="alquiler_urbano">Alquiler inmueble urbano (19%) → Modelo 115</SelectItem>
                    <SelectItem value="trabajo">Rendimiento del trabajo (nómina) → Modelo 111</SelectItem>
                    <SelectItem value="ninguno">Sin clasificar / Otro</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-amber-700 mt-1.5">Determina en qué modelo trimestral (111 o 115) aparecerá esta retención.</p>
              </div>
            )}
            {/* NEW · Multi-IVA: tramos adicionales en la misma factura */}
            <div className="border border-dashed border-[var(--ag-border)] rounded-sm p-3 bg-[var(--ag-midnight-soft)]/40" data-testid="exp-vat-lines-section">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-aqua-soft)] font-semibold">Tramos IVA adicionales</div>
                  <div className="text-[11px] text-[var(--ag-text-dim)]">Para facturas con 2-3 IVAs (ej. parte 21% + parte 10%).</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addVatLine} className="rounded-sm h-8" data-testid="exp-add-vat-line">
                  <Plus size={12} className="mr-1" /> Añadir tramo IVA
                </Button>
              </div>
              {(form.vat_lines || []).length === 0 ? (
                <div className="text-[11px] text-[var(--ag-text-dim)] text-center py-2 italic">Sin tramos extra · La factura usa solo el IVA principal.</div>
              ) : (
                <div className="space-y-1.5">
                  {form.vat_lines.map((v, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`exp-vat-row-${idx}`}>
                      <div className="col-span-1 text-[10px] text-[var(--ag-text-dim)] text-center">#{idx + 2}</div>
                      <div className="col-span-5">
                        <Input type="number" step="0.01" value={v.base} placeholder="Base €"
                          onChange={(e)=>updateVatLine(idx, "base", e.target.value)}
                          className="h-9 rounded-sm tabular-nums" data-testid={`exp-vat-base-${idx}`} />
                      </div>
                      <div className="col-span-4">
                        <Select value={String(v.rate)} onValueChange={(val)=>updateVatLine(idx, "rate", Number(val))}>
                          <SelectTrigger className="h-9 rounded-sm" data-testid={`exp-vat-rate-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0% (Exento)</SelectItem>
                            <SelectItem value="4">4% (Súper-reducido)</SelectItem>
                            <SelectItem value="10">10% (Reducido)</SelectItem>
                            <SelectItem value="21">21% (General)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 text-right">
                        <Button type="button" size="icon" variant="ghost" onClick={()=>removeVatLine(idx)} className="hover:bg-red-500/10 h-9 w-9" data-testid={`exp-vat-del-${idx}`}>
                          <X size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="text-[10px] text-[var(--ag-text-dim)] mt-2 tabular-nums">
                    Base total = principal + extras · IVA total agregado se calcula automáticamente al guardar.
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider">Proveedor</Label>
                <div className="flex gap-1 mt-1">
                  <Select value={form.provider_id || "__none__"} onValueChange={(v)=>setForm({...form, provider_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger className="rounded-sm" data-testid="exp-provider"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin proveedor</SelectItem>
                      {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={()=>setProvOpen(true)} className="shrink-0" data-testid="exp-new-provider"><Plus size={14} /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                <Select value={form.category || "__none__"} onValueChange={(v)=>setForm({...form, category: v === "__none__" ? "" : v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Libre</SelectItem>
                    {FIXED_OPERATIONAL_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer p-3 border border-amber-400/30 bg-amber-500/5 rounded-sm" data-testid="exp-fixed-toggle">
              <input
                type="checkbox"
                checked={!!form.is_fixed_operational}
                onChange={(e)=>setForm({...form, is_fixed_operational: e.target.checked})}
                className="h-4 w-4 accent-amber-500"
              />
              <Lightning size={14} weight="fill" className="text-amber-500" />
              <span className="text-sm">
                Es un <b>Gasto Fijo Operativo</b>
                <span className="block text-[11px] text-gray-500 normal-case">Alquiler, luz, agua, basura, seguros, internet…</span>
              </span>
            </label>
            <div>
              <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5"><Paperclip size={11} /> Adjuntar factura (PDF o imagen · máx 3 MB)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="file" accept="application/pdf,image/*" onChange={onFile} className="rounded-sm text-xs" data-testid="exp-file-input" />
                {form.attachment_url && (
                  <Button type="button" size="icon" variant="ghost" onClick={()=>setForm({...form, attachment_url: "", attachment_filename: ""})} title="Quitar adjunto" data-testid="exp-file-remove">
                    <X size={14} />
                  </Button>
                )}
              </div>
              {form.attachment_filename && (
                <div className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1.5">
                  <CheckLikeIcon /> {form.attachment_filename}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.concept} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="exp-submit">
              {busy ? "Guardando…" : (editingId ? "Actualizar" : "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal alta rápida proveedor */}
      <Dialog open={provOpen} onOpenChange={setProvOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="exp-prov-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tighter">Nuevo proveedor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={newProv.name} onChange={(e)=>setNewProv({...newProv, name: e.target.value})} className="mt-1 rounded-sm" data-testid="exp-prov-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">CIF/NIF</Label>
                <Input value={newProv.nif_cif} onChange={(e)=>setNewProv({...newProv, nif_cif: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <Input value={newProv.phone} onChange={(e)=>setNewProv({...newProv, phone: e.target.value})} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" value={newProv.email} onChange={(e)=>setNewProv({...newProv, email: e.target.value})} className="mt-1 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setProvOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={createProvider} className="ag-btn-gold" data-testid="exp-prov-submit">Crear y seleccionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CheckLikeIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M13.78 4.22a.75.75 0 010 1.06l-7 7a.75.75 0 01-1.06 0l-3-3a.75.75 0 011.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 011.06 0z" />
    </svg>
  );
}
